import {useTranslation} from "react-i18next";
import {NameWithId} from "@utils/Models";
import {GUID} from "@utils/AppConstants";
import FlintDropDownList from "@components/controls/FlintDropDownList";
import FlintTextBox from "@components/controls/FlintTextBox";
import FlintButton_Cancel from "@components/controls/buttons/FlintButton_Cancel";
import FlintButton_Accept from "@components/controls/buttons/FlintButton_Accept";
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import {SubstrateOption, useRecipeDescriptionGeneratorPanel} from "@pages/orders/preview/recipes/edit/description_generator/useRecipeDescriptionGeneratorPanel";
import styles from "./RecipeDescriptionGeneratorPanel.module.scss";

export default function RecipeDescriptionGeneratorPanel(props: RecipeDescriptionGeneratorPanelProps) {
    const {state, handlers, utilities} = useRecipeDescriptionGeneratorPanel(props);
    const {t} = useTranslation();

    const parts = state.previewParts.get;
    const hasAny = parts.inkSerie || parts.colourStandardName || parts.itemTypeSymbol || parts.substrateSymbol;

    return (
        <FlintLoadingWrapper showLoading={state.isLoading.get}>
            <div className={styles.panel}>
                <div className={styles.inputs}>
                    <div className={`block-view ${styles.inkSerieField}`}>
                        <FlintDropDownList<NameWithId>
                            dataSource={state.inkSerieOptions.get}
                            selectedValue={state.selectedInkSerie.get}
                            onItemSelected={handlers.handleInkSerieChanged}
                            label={t("RECIPES_DescriptionGenerator_InkSeries")}/>
                    </div>
                    <div className={`block-view ${styles.csNameField}`}>
                        <FlintTextBox
                            value={state.colourStandardName.get}
                            onChange={e => handlers.handleColourStandardNameChanged(e.value ?? "")}
                            label={t("RECIPES_DescriptionGenerator_ColourStandardName")}
                            maxLength={100}/>
                    </div>
                    <div className={`block-view ${styles.itemTypeField}`}>
                        <FlintDropDownList<NameWithId>
                            dataSource={state.itemTypeOptions.get}
                            selectedValue={state.selectedColourType.get}
                            onItemSelected={handlers.handleColourTypeChanged}
                            label={t("RECIPES_DescriptionGenerator_ItemType")}/>
                    </div>
                    <div className={`block-view ${styles.substrateField}`}>
                        <FlintDropDownList<SubstrateOption>
                            dataSource={state.substrateOptions.get}
                            selectedValue={state.selectedSubstrate.get}
                            onItemSelected={handlers.handleSubstrateChanged}
                            label={t("RECIPES_DescriptionGenerator_Substrate")}/>
                    </div>
                </div>

                <div className={styles.previewSection}>
                    <div className={styles.previewLabel}>
                        {t("RECIPES_DescriptionGenerator_PreviewLabel")}
                    </div>
                    <div className={styles.previewValue}>
                        {hasAny ? (
                            <>
                                {parts.inkSerie && <span className={styles.partInkSerie}>{parts.inkSerie}</span>}
                                {parts.inkSerie && parts.colourStandardName && " "}
                                {parts.colourStandardName && <span className={styles.partCsName}>{parts.colourStandardName}</span>}
                                {parts.colourStandardName && parts.itemTypeSymbol && " "}
                                {parts.itemTypeSymbol && <span className={styles.partItemType}>{parts.itemTypeSymbol}</span>}
                                {(parts.inkSerie || parts.colourStandardName || parts.itemTypeSymbol) && parts.substrateSymbol && " "}
                                {parts.substrateSymbol && <span className={styles.partSubstrate}>{parts.substrateSymbol}</span>}
                            </>
                        ) : (
                            <span className={styles.previewEmpty}>—</span>
                        )}
                    </div>
                    {utilities.willTruncate && (
                        <div className={styles.truncatedHint}>
                            {t("RECIPES_DescriptionGenerator_TruncatedHint")}
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    <FlintButton_Cancel onClick={handlers.handleCancel}/>
                    <FlintButton_Accept onClick={handlers.handleAccept}/>
                </div>
            </div>
        </FlintLoadingWrapper>
    );
}

export interface RecipeDescriptionGeneratorPanelProps {
    OrderGuid: GUID;
    InitialInkSerieId?: number;
    InitialSubstrateId?: number;
    InitialColourStandardName?: string;
    InitialColourType?: NameWithId | null;
    OnAccepted: (description: string) => void;
    OnCancelled: () => void;
}
